/**
 * OnboardingSystem.ts
 * First-run walkthrough — a deck of pointer cards that introduce the beacon,
 * each band member (with their skill), venues, and the core win condition.
 *
 * Shown only once: completion is persisted in localStorage under
 * ONBOARDING_KEY. Calling `show()` when already completed is a no-op.
 *
 * Each card floats near the bottom of the screen (above the command panel),
 * in line with the unit it describes. Click NEXT or press SPACE/Enter to
 * advance; SKIP closes the deck immediately.
 */

import { Container, Graphics, Text, TextStyle, Ticker } from 'pixi.js';

// ── Persistence ────────────────────────────────────────────────────────────
const ONBOARDING_KEY = 'future_beacon_v1_onboarded';

// ── Card layout ────────────────────────────────────────────────────────────
const CARD_W   = 500;
const CARD_H   = 170;
const CARD_PAD = 22;
const BAR_H    = 4;

// ── Text styles ────────────────────────────────────────────────────────────
const S_STEP = new TextStyle({
  fontFamily: 'Menlo, Consolas, monospace',
  fontSize:   9,
  fill:       '#3d6660',
  letterSpacing: 1,
});

const S_TITLE = new TextStyle({
  fontFamily: 'Menlo, Consolas, monospace',
  fontSize:   13,
  fontWeight: 'bold',
  fill:       '#c9fff8',
  letterSpacing: 1,
});

const S_BODY = new TextStyle({
  fontFamily: 'Georgia, "Times New Roman", serif',
  fontSize:   13,
  fill:       '#8aaaa5',
  lineHeight: 20,
  wordWrap:   true,
  wordWrapWidth: CARD_W - CARD_PAD * 2 - 22,
});

const S_SKILL = new TextStyle({
  fontFamily: 'Menlo, Consolas, monospace',
  fontSize:   10,
  fill:       '#5a9090',
  letterSpacing: 0.5,
  wordWrap:   true,
  wordWrapWidth: CARD_W - CARD_PAD * 2 - 22,
});

const S_BTN = new TextStyle({
  fontFamily: 'Menlo, Consolas, monospace',
  fontSize:   11,
  fill:       '#00ffcc',
  letterSpacing: 2,
});

const S_SKIP = new TextStyle({
  fontFamily: 'Menlo, Consolas, monospace',
  fontSize:   10,
  fill:       '#2a4a46',
  letterSpacing: 1,
});

// ── Card data ─────────────────────────────────────────────────────────────

interface Card {
  colour:  number;
  title:   string;
  body:    string;
  skill?:  string;   // e.g. "[ Q ]  Power Chord — stun nearby enemies"
}

const CARDS: Card[] = [
  {
    colour: 0x00ffcc,
    title:  'THE FUTURE BEACON',
    body:   'That glowing cube at the centre is the Future Beacon. It broadcasts music from the future. If its signal hits zero the show is over — protect it at all costs.',
  },
  {
    colour: 0x22ddcc,
    title:  'FANS',
    body:   'Fans are your main force. They mine GEAR from deposit nodes on the map and fight enemies that get too close. Build more from the command panel using GEAR.',
  },
  {
    colour: 0x4488ff,
    title:  'STEVE & THE GUITAR GANG  ·  Protector',
    body:   'High HP brawler — keeps enemies off the back line and shields nearby allies from heavy hits.',
    skill:  '[ Q ]  Power Chord — stun all nearby enemies for 2.5 seconds',
  },
  {
    colour: 0xff44aa,
    title:  'ALI & THE VOCAL MOB  ·  Promoter',
    body:   'Drives fan numbers up and buffs crowd momentum. Best positioned near mining fans to keep REP and SALES ticking.',
    skill:  '[ Q ]  Crowd Hype — speed boost ×1.6 for nearby allies',
  },
  {
    colour: 0xff8833,
    title:  'CHRIS & THE RHYTHM CREW  ·  Engine',
    body:   'Chris powers the tempo. Every hit he takes slows the whole band down — if he falls, attacks crawl. Guard him well.',
    skill:  '[ Q ]  Tempo Surge — attack-rate boost ×1.75 for all allies',
  },
  {
    colour: 0x44cc66,
    title:  'ALEX & THE BASS LINE  ·  Sustain',
    body:   'Keeps the band in the fight. Move Alex near a downed band member to automatically revive them. Burst-heal when the going gets tough.',
    skill:  '[ Q ]  Bassline — heal 30 % max-HP for all nearby allies',
  },
  {
    colour: 0xaa44ff,
    title:  'JIM & THE KEYBOARD PROPHETS  ·  Mystic',
    body:   'Extends your line of sight through the fog. Enemies you cannot see are enemies you cannot intercept in time.',
    skill:  '[ Q ]  Prophecy — instantly reveal a 10-tile radius of fog',
  },
  {
    colour: 0xffd700,
    title:  'VENUES',
    body:   'Diamond shapes on the map are venues. Send fans there to claim them — claimed venues boost your signal, REP, and SALES. Enemies will attack them, so defend what you own.',
  },
  {
    colour: 0xe8c84a,
    title:  'NIGEL & THE SONG CATCHERS  ·  Songwriter',
    body:   'Nigel receives transmissions from the future — song fragments that teach the band what\'s coming. He fights at the front but can broadcast a verse to protect his allies.',
    skill:  '[ Q ]  Future Verse — stuns nearby enemies + heals nearby allies for 20 % HP',
  },
  {
    colour: 0x00ffcc,
    title:  "YOU'RE READY",
    body:   'Protect the Signal. Claim the Venues. Save the Scene.',
  },
];

// ── Fade duration ──────────────────────────────────────────────────────────
const FADE_IN  = 0.18; // seconds
const FADE_OUT = 0.22;

// ── Class ─────────────────────────────────────────────────────────────────

export class OnboardingSystem {
  private readonly overlay   = new Container();
  private readonly card      = new Container();
  private screenW  = 1280;
  private screenH  = 720;
  private index    = 0;
  private done     = false;
  private onDone?: () => void;
  private fadeDir  = 1;   // +1 = in, -1 = out, 0 = stable
  private elapsed  = 0;

  /** True when onboarding has already been completed and the overlay is hidden. */
  get isComplete(): boolean { return this.done; }

  /** Clear the completed flag so the deck shows again on the next show() call. */
  reset(): void {
    localStorage.removeItem(ONBOARDING_KEY);
    this.done = false;
  }

  constructor(private readonly ui: Container) {
    this.overlay.addChild(this.card);
    ui.addChild(this.overlay);
    this.overlay.visible = false;
  }

  /**
   * Show the onboarding deck.
   * Silently returns if already completed (localStorage flag set).
   * `onComplete` is called when the player finishes or skips all cards.
   */
  show(w: number, h: number, onComplete?: () => void): void {
    if (localStorage.getItem(ONBOARDING_KEY) === '1') {
      this.done = true;
      return;
    }
    this.screenW  = w;
    this.screenH  = h;
    this.onDone   = onComplete;
    this.index    = 0;
    this.done     = false;
    this.fadeDir  = 1;
    this.elapsed  = 0;
    this.overlay.alpha   = 0;
    this.overlay.visible = true;

    this.buildCard();
  }

  resize(w: number, h: number): void {
    this.screenW = w;
    this.screenH = h;
    if (this.overlay.visible && !this.done) this.buildCard();
  }

  /** Drive fade animations — call every game-loop frame. */
  update(delta: number): void {
    if (!this.overlay.visible || this.done) return;

    if (this.fadeDir === 1) {
      this.elapsed += delta;
      this.overlay.alpha = Math.min(1, this.elapsed / FADE_IN);
      if (this.elapsed >= FADE_IN) {
        this.overlay.alpha = 1;
        this.fadeDir = 0;
      }
    } else if (this.fadeDir === -1) {
      this.elapsed += delta;
      this.overlay.alpha = Math.max(0, 1 - this.elapsed / FADE_OUT);
      if (this.elapsed >= FADE_OUT) {
        this.overlay.alpha   = 0;
        this.overlay.visible = false;
        this.done = true;
        localStorage.setItem(ONBOARDING_KEY, '1');
        this.onDone?.();
      }
    }
  }

  /** Keyboard shortcut — SPACE or Enter advances the current card. */
  handleKey(key: string): boolean {
    if (this.done || !this.overlay.visible) return false;
    if (key === ' ' || key === 'Enter') {
      this.advance();
      return true;
    }
    return false;
  }

  // ── Private ───────────────────────────────────────────────────────────────

  private advance(): void {
    this.index++;
    if (this.index >= CARDS.length) {
      this.startFade();
    } else {
      this.elapsed = 0;
      this.fadeDir = 0;
      this.overlay.alpha = 1;
      this.buildCard();
    }
  }

  private skip(): void {
    this.startFade();
  }

  private startFade(): void {
    this.elapsed = 0;
    this.fadeDir = -1;
  }

  private buildCard(): void {
    this.card.removeChildren();
    const card = CARDS[this.index];

    // ── Responsive card width: cap to screen width with 32px margins ───────
    const cardW = Math.min(CARD_W, this.screenW - 32);
    const cardH = CARD_H;
    const wrapW = cardW - CARD_PAD * 2 - 22;

    // ── Position card: centred horizontally, ~180px above bottom ───────────
    const cx = (this.screenW - cardW) / 2;
    const cy = this.screenH - cardH - 120;
    this.card.position.set(cx, cy);

    const col     = card.colour;
    const colHex  = `#${col.toString(16).padStart(6, '0')}`;
    const isLast  = this.index === CARDS.length - 1;

    // ── Background ────────────────────────────────────────────────────────
    const bg = new Graphics();
    bg.beginFill(0x060d0b, 0.97);
    bg.lineStyle(1, col, 0.35);
    bg.drawRoundedRect(0, 0, cardW, cardH, 4);
    bg.endFill();
    bg.lineStyle(0);
    this.card.addChild(bg);

    // Colour bar at top
    const bar = new Graphics();
    bar.beginFill(col, 0.75);
    bar.drawRoundedRect(0, 0, cardW, BAR_H, 2);
    bar.endFill();
    this.card.addChild(bar);

    // Make the whole card non-interactive for underlying map clicks
    bg.eventMode = 'static';

    // ── Step counter (top-right) ─────────────────────────────────────────
    const stepTxt = new Text(`${this.index + 1} / ${CARDS.length}`, S_STEP);
    stepTxt.anchor.set(1, 0);
    stepTxt.position.set(cardW - CARD_PAD, BAR_H + 8);
    this.card.addChild(stepTxt);

    // ── Colour dot + title ───────────────────────────────────────────────
    const dot = new Graphics();
    dot.beginFill(col, 0.9);
    dot.drawCircle(0, 0, 5);
    dot.endFill();
    dot.position.set(CARD_PAD, BAR_H + 18);
    this.card.addChild(dot);

    const title = new Text(card.title, { ...S_TITLE, fill: colHex } as TextStyle);
    title.position.set(CARD_PAD + 14, BAR_H + 11);
    this.card.addChild(title);

    // ── Body text ────────────────────────────────────────────────────────
    // Clone styles so wordWrapWidth adapts to the actual card width
    const bodyStyle = S_BODY.clone();
    bodyStyle.wordWrapWidth = wrapW;
    const skillStyle = S_SKILL.clone();
    skillStyle.wordWrapWidth = wrapW;

    let bodyY = BAR_H + 36;
    const body = new Text(card.body, bodyStyle);
    body.position.set(CARD_PAD, bodyY);
    this.card.addChild(body);
    bodyY += body.height + 6;

    // ── Skill line (if any) ──────────────────────────────────────────────
    if (card.skill) {
      const skillTxt = new Text(card.skill, skillStyle);
      skillTxt.position.set(CARD_PAD, bodyY + 2);
      this.card.addChild(skillTxt);
    }

    // ── Buttons ──────────────────────────────────────────────────────────
    const btnW  = isLast ? 100 : 80;
    const btnH  = 26;
    const btnY  = cardH - btnH - 12;

    // NEXT / START button
    const nextBg = new Graphics();
    const nextLbl = new Text(isLast ? 'START' : 'NEXT →', S_BTN);
    nextLbl.anchor.set(0.5, 0.5);

    const drawNextBtn = (hover: boolean) => {
      nextBg.clear();
      nextBg.beginFill(hover ? 0x0d2d25 : 0x030f0b, 0.95);
      nextBg.lineStyle(1, col, hover ? 0.9 : 0.5);
      nextBg.drawRoundedRect(0, 0, btnW, btnH, 3);
      nextBg.endFill();
    };

    const nextX = cardW - CARD_PAD - btnW;
    nextBg.position.set(nextX, btnY);
    drawNextBtn(false);
    nextBg.eventMode = 'static';
    nextBg.cursor    = 'pointer';
    nextBg.on('pointerover',  () => { drawNextBtn(true);  nextLbl.style.fill = '#ffffff'; });
    nextBg.on('pointerout',   () => { drawNextBtn(false); nextLbl.style.fill = '#00ffcc'; });
    nextBg.on('pointerdown',  () => this.advance());
    this.card.addChild(nextBg);

    nextLbl.position.set(nextX + btnW / 2, btnY + btnH / 2);
    this.card.addChild(nextLbl);

    // SKIP button (hidden on last card)
    if (!isLast) {
      const skipLbl = new Text('SKIP ALL', S_SKIP);
      skipLbl.anchor.set(0, 0.5);
      skipLbl.position.set(CARD_PAD, btnY + btnH / 2);
      skipLbl.eventMode = 'static';
      skipLbl.cursor = 'pointer';
      skipLbl.on('pointerover',  () => { skipLbl.style.fill = '#5a7a76'; });
      skipLbl.on('pointerout',   () => { skipLbl.style.fill = '#2a4a46'; });
      skipLbl.on('pointerdown',  () => this.skip());
      this.card.addChild(skipLbl);
    }
  }
}
